import {
  handleResp,
  getURLWithQueryParams,
  getDefaultHeaders,
  extendFetchOptions,
  handleCashifyBlobResp,
} from "./utils";
import type {
  WrappedCashifyFunction,
  WrappedCashifyPageableQuery,
  GenericResponse,
  CommonResponse,
  Sortable,
} from "./utils";
import type { StandardObject } from "utils";

export interface EkycService {
  listEkycHistory: ReturnType<typeof listEkycHistory>;
  getEkycDetail: ReturnType<typeof getEkycDetail>;
  getEkycImages: ReturnType<typeof getEkycImages>;
  getEkycRawResult: ReturnType<typeof getEkycRawResult>;
  putEkycReviewStatus: ReturnType<typeof putEkycReviewStatus>;
  generateEkycReport: ReturnType<typeof generateEkycReport>;
  synchronizeProviderStatus: ReturnType<typeof synchronizeProviderStatus>;
  getServiceSummary: ReturnType<typeof getServiceSummary>;
  getRequestSummary: ReturnType<typeof getRequestSummary>;
}

export type EkycProvider = "zoloz" | "worldview";

export type EkycTransactionType = "EKYC" | "FACE_CAPTURE" | "FACE_COMPARE";

export type EkycTransactionChannel = "APP_SDK" | "WEB_SDK" | "UNKNOWN";

export type EkycIdentityType =
  | "HKID"
  | "PASSPORT"
  | "CHINA_ID"
  | "SINGAPORE_CITIZEN_CARD"
  | "MALAYSIA_MY_KAD"
  | "OTHER" // Dummy type for frontend use;

export type EkycStatus =
  | "CREATED"
  | "PENDING_SYSTEM_PROCESS"
  | "CANCELLED"
  | "TIMEOUT"
  | "PROCESSED"
  | "REVIEWED"
  | "ERROR";

export type EkycRiskLevel = "LOW" | "MEDIUM" | "HIGH";

export type EkycReviewStatus = "ACCEPTED" | "UNCERTAIN" | "REJECTED";

// EKYC
export interface EkycRequestSearchCriteria extends Sortable {
  id?: number;
  userId?: number;
  provider?: EkycProvider[];
  transactionId?: string;
  transactionType?: EkycTransactionType[];
  businessReferenceId?: string;
  identityType?: EkycIdentityType;
  identityNumber?: string;
  status?: EkycStatus[];
  riskLevel?: EkycRiskLevel[];
  whitelisted?: boolean;
  originalRiskLevel?: EkycRiskLevel[];
  reviewStatus?: EkycReviewStatus[];
  "reviewTime.from"?: string;
  "reviewTime.to"?: string;
  reviewAdmin?: string;
  resultDataType?: string;
  "createTime.from"?: string;
  "createTime.to"?: string;
  "lastUpdateTime.from"?: string;
  "lastUpdateTime.to"?: string;
  lastUpdateAdmin?: string;
}

export interface EkycRequestDTO {
  id: number;
  tenantId: number;
  userId: number;
  businessReferenceId: string;
  provider: EkycProvider;
  transactionId: string;
  transactionType: EkycTransactionType;
  transactionChannel: EkycTransactionChannel;
  identityType: EkycIdentityType;
  identityNumberDisplay: string;
  status: EkycStatus;
  riskLevel: EkycRiskLevel;
  whitelisted: boolean;
  originalRiskLevel: EkycRiskLevel;
  reviewStatus: EkycReviewStatus;
  reviewStatusReason: string;
  reviewTime: string;
  reviewAdmin: string;
  createTime: string;
  lastUpdateTime?: string;
  lastUpdateAdmin?: string;
}

export interface EkycRequestDetailResult extends EkycRequestDTO {
  resultDataType?: string;
  personalInfo?: EkycPersonalInfo;
  userSubmittedInfo?: EkycPersonalInfo;
  clientData?: EkycClientData
}

export type EkycPersonalInfo = CommonEkycPersonalInfo & EkycHkidInfo;

export interface EkycClientData {
  ipAddress: string;
  geoLocation: {
    latitude: number;
    longitude: number;
  };
  deviceInfo: {
    deviceBrand: string;
    deviceModel: string;
    deviceOS: string;
    deviceOSVersion: string;
    isMobile: boolean;
    browser: string;
    browserVersion: string;
  };
  createTime: string;
}
interface CommonEkycPersonalInfo {
  enFirstName?: string;
  enLastName?: string;
  enFullName?: string;
  zhFullName?: string;
  sexual?: string;
  dateOfBirth?: string;
  identityType?: EkycIdentityType;
  identityNumber?: string;
  identityIssueDate?: string;
  identityExpiryDate?: string;
  identityFirstIssueDate?: string;
}

interface EkycHkidInfo {
  identitySymbols?: string;
  permanentResidentStatus?: "true" | "false";
  chineseCommercialCode?: string;
}

export type EkycImageType =
  | "IDENTITY_FRONT"
  | "IDENTITY_BACK"
  | "IDENTITY_FRONT_CROPPED"
  | "IDENTITY_BACK_CROPPED"
  | "IDENTITY_FRONT_FLASHLIGHT"
  | "IDENTITY_BACK_FLASHLIGHT"
  | "IDENTITY_FRONT_ANGLED"
  | "IDENTITY_BACK_ANGLED"
  | "IDENTITY_OTHER"
  | "FACE_FRONT"
  | "FACE_FRONT_CROPPED"
  | "FACE_FRONT_EYES_CLOSED"
  | "FACE_OTHER"
  | "OTHER";

export interface EkycImageDTO {
  type: EkycImageType;
  encodedContent: string;
}

export interface EkycPutReviewStatusRequest {
  id: number;
  detail: {
    reviewStatus?: EkycReviewStatus;
    reviewStatusReason?: string;
  };
}

export interface EkycSynchronizeProviderStatusRequest {
  id: number;
  ekycStatus: EkycStatus;
}
export interface EkycServiceSummaryResult {
  overallBreakdownByTypeAndStatus: {
    [k in EkycTransactionType]: {
      [s in EkycStatus]: number;
    };
  };
}

export interface EkycRequestSummaryResult {
  userId: number;
  provider: EkycProvider;
  transactionType: EkycTransactionType;
  transactionChannel: EkycTransactionChannel;
  identityType: EkycIdentityType;
  status: EkycStatus;
  riskLevel: EkycRiskLevel;
  whitelisted: boolean;
  originalRiskLevel: EkycRiskLevel;
  reviewStatus: EkycReviewStatus;
  createTime: string;
  createDate: string;
}

export const listEkycHistory: WrappedCashifyPageableQuery<
  EkycRequestSearchCriteria,
  EkycRequestDTO
> = ({ urlPrefix, options }) => {
  return async (queryParams) => {
    let url = getURLWithQueryParams(`${urlPrefix}/api/v1/ekyc`, queryParams);
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const getEkycDetail: WrappedCashifyFunction<
  { id: number },
  GenericResponse<EkycRequestDetailResult>
> = ({ urlPrefix, options }) => {
  return async ({ id }) => {
    let url = `${urlPrefix}/api/v1/ekyc/${id}`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const getEkycRawResult: WrappedCashifyFunction<
  { id: number },
  GenericResponse<StandardObject>
> = ({ urlPrefix, options }) => {
  return async ({ id }) => {
    let url = `${urlPrefix}/api/v1/ekyc/${id}/provider-raw-result`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const getEkycImages: WrappedCashifyFunction<
  { id: number },
  GenericResponse<EkycImageDTO[]>
> = ({ urlPrefix, options }) => {
  return async ({ id }) => {
    let url = `${urlPrefix}/api/v1/ekyc/${id}/images`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const putEkycReviewStatus: WrappedCashifyFunction<
  EkycPutReviewStatusRequest,
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ id, detail }) => {
    let url = `${urlPrefix}/api/v1/ekyc/${id}/review`;
    let opts = extendFetchOptions(
      {
        method: "PUT",
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(detail),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const generateEkycReport: WrappedCashifyFunction<
  { id: number; signal?: AbortSignal },
  { blob: Blob; name: string }
> = ({ urlPrefix, options }) => {
  return async ({ id, signal }) => {
    let url = `${urlPrefix}/api/v1/ekyc/${id}/report`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: {
          Accept: "application/octet-stream",
        },
        signal,
      },
      options
    );
    return fetch(url, opts).then((resp) => handleCashifyBlobResp(resp));
  };
};

export const synchronizeProviderStatus: WrappedCashifyFunction<
  EkycSynchronizeProviderStatusRequest,
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ id, ekycStatus }) => {
    let url: string;
    switch (ekycStatus) {
      case "CREATED":
        url = `${urlPrefix}/api/v1/ekyc/${id}/synchronize-provider-status`;
        break;
      case "PENDING_SYSTEM_PROCESS":
        url = `${urlPrefix}/api/v1/ekyc/${id}/synchronize-provider-status`;
        break;
      default:
        url = getURLWithQueryParams(
          `${urlPrefix}/api/v1/ekyc/${id}/synchronize-provider-status`,
          { updateFinal: true }
        );
        break;
    }
    let opts = extendFetchOptions(
      {
        method: "POST",
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
        },
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};
export const getServiceSummary: WrappedCashifyFunction<
  void,
  GenericResponse<EkycServiceSummaryResult>
> = ({ urlPrefix, options }) => {
  return async () => {
    let url = `${urlPrefix}/api/v1/ekyc/overall-summary`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const getRequestSummary: WrappedCashifyFunction<
  { from: string; to: string },
  GenericResponse<EkycRequestSummaryResult[]>
> = ({ urlPrefix, options }) => {
  return async (queryParams) => {
    let url = getURLWithQueryParams(
      `${urlPrefix}/api/v1/ekyc/request-summary`,
      queryParams
    );
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};
